import React, { useContext, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  addIndex,
  append,
  includes,
  last,
  lensPath,
  lensProp,
  map,
  pipe,
  prop,
  remove,
  set,
  sortBy,
  update,
} from "ramda";
import { notBlank, notEmpty } from "../../../util/ext";
import { formatContributorName } from "../../../util/stringFormat";
import clsx from "clsx";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import FormLabel from "@mui/material/FormLabel";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/EditOutlined";
import ContributorDialog from "../../content/edit/ContributorDialog";
import { MediaContext } from "../../common/media-context";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    padding: 0,
    color: theme.palette.primary.darker,
  },

  button: {
    padding: 8,
  },

  header: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "black",
  },

  contributorsContainer: {
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.42)",
  },

  contributorsContainerError: {
    borderBottomColor: theme.palette.error.main,
  },

  contributorsError: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },

  contributorRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
  },

  contributorName: {
    lineHeight: "1.8rem",
    minWidth: 300,
  },

  contributorActions: {
    minWidth: 100,
  },
}));

const Contributors = () => {
  const classes = useStyles();
  const context = useContext(MediaContext);

  const [required, setRequired] = useState(true);
  useEffect(() => {
    const type = context.media.type;
    const required = includes("categories", context.requiredFields[type]);
    setRequired(required);
  }, [context.requiredFields, context.media.type]);

  const [dialog, setDialog] = useState({
    visible: false,
    index: null,
    contributor: {
      contributorType: "PERSON",
      firstName: "",
      lastName: "",
    },
  });

  const handleEditContributor = (index, contributor) => () => {
    console.log("Edit Contributor!");
    console.log(`index : ${index}`);
    setDialog({
      visible: true,
      title: "Edit Contributor",
      index: index,
      contributor: contributor,
    });
  };

  const handleRemoveContributor = index => () => {
    console.log("Remove Contributor!");
    context.setMedia(prev => {
      const newList = remove(index, 1, prev.contributors);
      return set(lensPath(["contributors"]), newList, prev);
    });
  };

  const handleAddContributor = () => {
    console.log("Add Contributor!");
    setDialog({
      visible: true,
      title: "Add a New Contributor",
      index: null,
      contributor: {
        contributorType: "PERSON",
        firstName: "",
        lastName: "",
      },
    });
  };

  const handleContributorCancel = () => {
    console.log("Cancel Contributor!");
    setDialog({
      visible: false,
      index: null,
      contributor: {},
    });
  };

  const handleContributorSave = (contrib, index) => {
    console.log("Save Contributor!");
    console.log(`Index : ${index}`);

    context.setMedia(prev => {
      if (notBlank(index)) {
        const newList = update(index, contrib, prev.contributors);
        return set(lensPath(["contributors"]), newList, prev);
      } else {
        const lastDisplayOrder = pipe(
          sortBy(prop("displayOrder")),
          last,
          prop("displayOrder"),
        )(prev.contributors);
        const newList = append(
          set(lensProp("displayOrder"), lastDisplayOrder + 1, contrib),
          prev.contributors,
        );
        return set(lensPath(["contributors"]), newList, prev);
      }
    });

    setDialog({
      visible: false,
      index: null,
      contributor: {},
    });
  };

  const createContributorRow = (onEditContributor, onRemoveContributor) => (
    contributor,
    index,
  ) => (
    <div
      key={`details-contributor-${index}`}
      className={classes.contributorRow}
    >
      <div className={classes.contributorName}>
        {formatContributorName(contributor)}
      </div>
      <div className={classes.contributorActions}>
        <IconButton
          style={{ padding: 9 }}
          aria-label="edit"
          onClick={onEditContributor(index, contributor)}
          size="large"
        >
          <EditIcon style={{ fontSize: "2rem" }} />
        </IconButton>
        <IconButton
          style={{ padding: 9 }}
          aria-label="delete"
          onClick={onRemoveContributor(index)}
          size="large"
        >
          <DeleteIcon style={{ fontSize: "2rem" }} />
        </IconButton>
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      <Grid item container justifyContent="space-between" alignItems="center">
        <FormLabel required={required} className={classes.header}>
          Contributors
        </FormLabel>
        <Button
          className={classes.button}
          variant="outlined"
          color="primary"
          onClick={handleAddContributor}
        >
          Add Contributor
        </Button>
      </Grid>
      {notBlank(context.validator.hasError(context.errors, "contributors")) && (
        <div className={classes.contributorsError}>
          {context.validator.firstError(context.errors, "contributors")}
        </div>
      )}
      {notEmpty(context.media) && (
        <div className={classes.form}>
          <Grid spacing={2} container alignItems="flex-end">
            <Grid item xs>
              <div
                className={clsx({
                  [classes.contributorsContainer]: notBlank(
                    context.media.contributors,
                  ),
                })}
              >
                {addIndex(map)(
                  createContributorRow(
                    handleEditContributor,
                    handleRemoveContributor,
                  ),
                  context.media.contributors,
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      )}
      <ContributorDialog
        visible={dialog.visible}
        title={dialog.title}
        contributor={dialog.contributor}
        index={dialog.index}
        onCancel={handleContributorCancel}
        onSave={handleContributorSave}
      />
    </div>
  );
};

export default Contributors;
